import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../apiService';
import { useLiveRefresh } from '../liveRefresh';

/**
 * Cinémas (2026-11-04) — vue admin : cinémas et activité 30 j, séances (filtre statut / ville), journal des
 * annulations (motif, remboursements). L'admin peut annuler une séance (rôle finance) : mêmes remboursements
 * que l'annulation par le cinéma. Les réglages (blocage des places, découvert guichet…) sont dans Paramètres.
 */
const STATUS_META = {
  on_sale: { label: 'En vente', bg: '#ECFDF5', color: '#166534' },
  closed: { label: 'Vente en ligne close', bg: '#FEF3C7', color: '#92400E' },
  played: { label: 'Jouée', bg: '#F4F5F7', color: '#5C636E' },
  cancelled: { label: 'Annulée', bg: '#fee2e2', color: '#991b1b' },
};
const fmt = (d) => { try { return new Date(d).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return '—'; } };
const money = (n) => `${Math.round(n || 0).toLocaleString('fr-FR')} CDF`;

export default function CinemaAdminPage() {
  const [tab, setTab] = useState('showtimes');
  const [status, setStatus] = useState('');
  const [summary, setSummary] = useState(null);
  const [showtimes, setShowtimes] = useState([]);
  const [cancellations, setCancellations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, st, c] = await Promise.all([
        apiService.get('/admin/cinema/summary', { userType: 'admin' }).catch(() => null),
        apiService.get(`/admin/cinema/showtimes${status ? `?status=${status}` : ''}`, { userType: 'admin' }).catch(() => null),
        apiService.get('/admin/cinema/cancellations', { userType: 'admin' }).catch(() => null),
      ]);
      setSummary(s?.success ? s : null);
      setShowtimes(st?.success ? st.showtimes : []);
      setCancellations(c?.success ? c.cancellations : []);
    } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);
  useLiveRefresh(() => load(), ['cinema_*']);

  const openDetail = async (id) => {
    setDetail({ loading: true });
    try {
      const r = await apiService.get(`/admin/cinema/showtimes/${id}`, { userType: 'admin' });
      if (r?.success) setDetail(r); else { setDetail(null); alert(r?.error || 'Erreur'); }
    } catch (e) { setDetail(null); alert(e?.error || 'Erreur'); }
  };

  const cancel = async (s) => {
    const reason = window.prompt(`Annuler la séance « ${s.movie} » du ${fmt(s.starts_at)} ? Les acheteurs seront remboursés sur leur solde et la commission guichet recréditée au cinéma.\n\nMotif :`, 'Annulée par Mbiyo');
    if (reason === null) return;
    setBusy(s.showtime_id);
    try {
      const r = await apiService.post(`/admin/cinema/showtimes/${s.showtime_id}/cancel`, { reason }, { userType: 'admin' });
      if (!r?.success) throw r;
      alert(`Séance annulée : ${r.refunded_orders} commande(s) remboursée(s), ${money(r.refunded_amount)}.`);
      setDetail(null); await load();
    } catch (e) { alert(e?.error || e?.data?.error || 'Action impossible'); }
    finally { setBusy(null); }
  };

  const Tabs = () => (
    <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
      {[['showtimes', 'Séances'], ['cinemas', 'Cinémas'], ['cancellations', `Annulations${cancellations.length ? ` (${cancellations.length})` : ''}`]].map(([k, l]) => (
        <button key={k} onClick={() => setTab(k)} style={{ ...tabBtn, ...(tab === k ? tabOn : {}) }}>{l}</button>
      ))}
    </div>
  );

  return (
    <div className="dashboard-content">
      <div className="page-header">
        <h1 className="page-title">🎬 Cinémas</h1>
        {tab === 'showtimes' && (
          <div className="page-actions">
            <select className="filter-select" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Toutes (14 jours)</option>
              <option value="on_sale">En vente</option>
              <option value="closed">Vente en ligne close</option>
              <option value="played">Jouées</option>
              <option value="cancelled">Annulées</option>
            </select>
          </div>
        )}
      </div>
      <Tabs />

      {loading ? (
        <div className="loading-spinner"><div className="spinner"></div><p>Chargement…</p></div>
      ) : tab === 'cinemas' ? (
        !summary?.cinemas?.length ? <p style={empty}>Aucun cinéma enregistré. Un commerce de type « Cinéma » apparaît ici dès son inscription.</p> : (
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Cinéma</th><th>Ville</th><th>Statut</th><th>Commission</th><th>Séances à venir</th><th>30 j : commandes</th><th>30 j : recette</th><th>dont guichet</th><th>30 j : commission</th></tr></thead>
              <tbody>
                {summary.cinemas.map((c) => (
                  <tr key={c.supplier_id}>
                    <td style={{ fontWeight: 600 }}>{c.business_name}</td>
                    <td>{c.city_slug}</td>
                    <td>{c.status}</td>
                    <td>{Math.round((c.commission_rate ?? 0.1) * 100)} %</td>
                    <td>{c.upcoming_showtimes}</td>
                    <td>{c.last30.orders}</td>
                    <td>{money(c.last30.gross)}</td>
                    <td>{money(c.last30.box_office)}</td>
                    <td style={{ color: '#C9350F', fontWeight: 600 }}>{money(c.last30.commission)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ ...empty, textAlign: 'left', padding: '0.75rem 0' }}>Séances annulées sur 30 jours : <b>{summary.cancelled_30d}</b>. Réglages (blocage des places, découvert guichet, tarif enfant…) : Paramètres plateforme → « 🎬 Cinémas ».</p>
          </div>
        )
      ) : tab === 'cancellations' ? (
        !cancellations.length ? <p style={empty}>Aucune séance annulée.</p> : (
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Cinéma</th><th>Film</th><th>Séance</th><th>Annulée le</th><th>Par</th><th>Motif</th><th>Places vendues</th><th>Remboursements</th></tr></thead>
              <tbody>
                {cancellations.map((c) => (
                  <tr key={c.showtime_id}>
                    <td style={{ fontWeight: 600 }}>{c.cinema_name || '—'}<div style={{ fontSize: '0.78rem', color: '#5C636E' }}>{c.city_slug}</div></td>
                    <td>{c.movie}</td>
                    <td style={{ fontSize: '0.85rem' }}>{fmt(c.starts_at)}</td>
                    <td style={{ fontSize: '0.85rem' }}>{fmt(c.cancelled_at)}</td>
                    <td>{c.cancelled_by === 'supplier' ? 'Le cinéma' : 'Mbiyo'}</td>
                    <td style={{ maxWidth: 260 }}>{c.reason || '—'}</td>
                    <td>{c.sold_count}</td>
                    <td>{c.refunded_orders} cde · {money(c.refunded_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : !showtimes.length ? <p style={empty}>Aucune séance pour ce filtre.</p> : (
        <div className="table-container">
          <table className="data-table">
            <thead><tr><th>Film</th><th>Cinéma</th><th>Salle</th><th>Séance</th><th>Places</th><th>Recette</th><th>Commission</th><th>Statut</th><th>Actions</th></tr></thead>
            <tbody>
              {showtimes.map((s) => {
                const st = STATUS_META[s.status] || STATUS_META.on_sale;
                return (
                  <tr key={s.showtime_id}>
                    <td><div onClick={() => openDetail(s.showtime_id)} style={{ cursor: 'pointer', fontWeight: 600, color: '#C9350F', textDecoration: 'underline' }}>{s.movie}</div><div style={{ fontSize: '0.78rem', color: '#5C636E' }}>{s.format} · {s.public_id}</div></td>
                    <td style={{ fontSize: '0.85rem' }}>{s.cinema_name || '—'}<div style={{ fontSize: '0.78rem', color: '#5C636E' }}>{s.city_slug}</div></td>
                    <td style={{ fontSize: '0.85rem' }}>{s.screen}<div style={{ fontSize: '0.78rem', color: '#5C636E' }}>{s.mode === 'free' ? 'placement libre' : 'numérotée'}</div></td>
                    <td style={{ fontSize: '0.85rem' }}>{fmt(s.starts_at)}</td>
                    <td style={{ fontWeight: 600 }}>{s.sold_count}/{s.capacity}</td>
                    <td>{money(s.gross)}</td>
                    <td style={{ color: '#C9350F' }}>{money(s.commission)}</td>
                    <td><span style={{ background: st.bg, color: st.color, padding: '3px 10px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap' }}>{st.label}</span>{s.cancel_reason && <div style={{ fontSize: '0.75rem', color: '#991b1b', marginTop: 3 }}>{s.cancel_reason}</div>}</td>
                    <td>{['on_sale', 'closed'].includes(s.status) && <button style={btn('#dc2626')} disabled={busy === s.showtime_id} onClick={() => cancel(s)}>Annuler</button>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <div style={overlay} onClick={() => setDetail(null)}>
          <div style={modalCard} onClick={(e) => e.stopPropagation()}>
            {detail.loading ? <div style={{ padding: 40, textAlign: 'center', color: '#5C636E' }}>Chargement…</div> : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '18px 20px', borderBottom: '1px solid #E9EBEF' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#1E2229' }}>{detail.showtime.movie_snapshot?.title}</h3>
                    <div style={{ fontSize: '0.85rem', color: '#5C636E' }}>{detail.showtime.cinema_name} · {detail.showtime.screen_snapshot?.name} · {fmt(detail.showtime.starts_at)} · {detail.showtime.format}</div>
                  </div>
                  <button onClick={() => setDetail(null)} style={{ background: 'none', border: 'none', fontSize: '1.6rem', cursor: 'pointer', color: '#9AA0A8', lineHeight: 1 }}>×</button>
                </div>
                <div style={{ padding: 20, overflowY: 'auto' }}>
                  <div style={statGrid}>
                    <Stat label="Places vendues" value={`${detail.showtime.sold_count}/${detail.showtime.capacity}`} />
                    <Stat label="Entrées scannées" value={detail.scanned} color="#166534" />
                    <Stat label="Commandes" value={detail.orders.length} />
                    <Stat label="Statut" value={(STATUS_META[detail.showtime.status] || {}).label || detail.showtime.status} />
                  </div>
                  <h4 style={{ margin: '18px 0 8px', fontSize: '0.95rem' }}>Commandes</h4>
                  <table className="data-table" style={{ width: '100%' }}>
                    <thead><tr><th>N°</th><th>Canal</th><th>Places</th><th>Confiserie</th><th>Total</th><th>Commission</th><th>Paiement</th></tr></thead>
                    <tbody>
                      {detail.orders.map((o) => (
                        <tr key={o.order_id}>
                          <td>{o.public_id || o.order_id.slice(-6)}</td>
                          <td>{o.channel === 'box_office' ? 'Guichet' : 'En ligne'}</td>
                          <td>{(o.items || []).map((i) => i.seat_code || i.tier_label).join(', ')}</td>
                          <td>{(o.concessions || []).map((c) => `${c.qty}× ${c.name}`).join(', ') || '—'}{o.concession_status && o.concession_status !== 'none' ? ` (${o.concession_status})` : ''}</td>
                          <td>{money(o.amount_total)}</td>
                          <td>{money(o.commission)}</td>
                          <td>{o.payment_method} · {o.payment_status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {['on_sale', 'closed'].includes(detail.showtime.status) && (
                  <div style={{ padding: '14px 20px', borderTop: '1px solid #E9EBEF', display: 'flex', justifyContent: 'flex-end' }}>
                    <button style={btn('#dc2626')} disabled={!!busy} onClick={() => cancel({ showtime_id: detail.showtime.showtime_id, movie: detail.showtime.movie_snapshot?.title, starts_at: detail.showtime.starts_at })}>Annuler la séance</button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ background: '#FFFFFF', border: '1px solid #E9EBEF', borderRadius: 10, padding: '10px 12px' }}>
      <div style={{ fontSize: '0.7rem', color: '#5C636E', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
      <div style={{ fontSize: '1.05rem', fontWeight: 800, color: color || '#1E2229', marginTop: 3 }}>{value}</div>
    </div>
  );
}

const btn = (color) => ({ background: color, color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' });
const tabBtn = { background: '#F4F5F7', border: '1px solid #E9EBEF', borderRadius: 999, padding: '6px 14px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', color: '#414852' };
const tabOn = { background: '#1E2229', color: '#fff', borderColor: '#1E2229' };
const empty = { color: '#9AA0A8', padding: '2rem', textAlign: 'center' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const modalCard = { background: '#fff', borderRadius: 14, width: 'min(860px, 96vw)', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' };
const statGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 };
